<script setup lang="ts">
import { ElMessageBox, ElNotification } from 'element-plus'
import { ContainerTagsApi } from '~/services/apiClient'
import { useContainerTagsStore } from '~/stores/containerTags'

const props = defineProps<{
  tag: string
}>()

const emit = defineEmits<{
  (e: 'tagDeleted', tag: string): void
  (e: 'tagRenamed', update: { oldTag: string; newTag: string }): void
}>()

const containerTagsStore = useContainerTagsStore()

function deleteTag(tagToDelete: string, event: MouseEvent) {
  event.stopPropagation()
  // deselect the tag
  ElMessageBox.confirm(
    `Are you sure you want to delete the tag "${tagToDelete}"?
    It will be removed from all containers and any capacity sets will be deleted.`,
    `Delete tag?`,
    {
      confirmButtonText: 'Yes, delete this tag',
      confirmButtonClass: 'el-button--danger',
      cancelButtonText: 'Cancel',
      type: 'warning',
    }
  ).then(() => {
    const api = new ContainerTagsApi()
    api
      .deleteTagContainersTagsTagNameDelete(tagToDelete)
      .then(() => {
        // remove the preset from the options
        containerTagsStore.tags = containerTagsStore.tags.filter(
          (tag) => tag !== tagToDelete
        )
        // emit event to notify parent component
        emit('tagDeleted', tagToDelete)
        // update the filters
        ElNotification({
          title: 'Tag deleted',
          type: 'success',
        })
      })
      .catch(() => {
        ElNotification({
          message: 'Error deleting tag',
          type: 'error',
        })
      })
  })
}

function renameTag(tagToEdit: string, event: MouseEvent) {
  event.stopPropagation()
  // deselect the tag
  ElMessageBox.prompt('Tag name:', `Edit the name of the tag "${tagToEdit}"`, {
    confirmButtonText: 'Rename Tag',
    cancelButtonText: 'Cancel',
    inputValue: tagToEdit,
  }).then(({ value }) => {
    const api = new ContainerTagsApi()
    api
      .renameTagContainersTagsTagNamePut(tagToEdit, { name: value })
      .then(() => {
        // rename the tag in the options
        containerTagsStore.tags = containerTagsStore.tags.map((tag) =>
          tag === tagToEdit ? value : tag
        )
        // emit event to notify parent component
        emit('tagRenamed', { oldTag: tagToEdit, newTag: value })
        ElNotification({
          title: 'Tag Renamed',
          type: 'success',
        })
      })
      .catch(() => {
        ElNotification({
          message: 'Error renaming tag',
          type: 'error',
        })
      })
  })
}
</script>

<template>
  <div class="w-full flex justify-between" role="option">
    <div>{{ props.tag }}</div>
    <div
      class="align-bottom align-right"
      style="position: relative; right: -15px"
    >
      <ElButton
        v-tooltip="`Rename tag ${tag}`"
        type="warning"
        size="small"
        circle
        plain
        @click.stop="(e) => renameTag(tag, e)"
      >
        <i-mdi:pencil />
      </ElButton>
      <ElButton
        v-tooltip="`Delete tag ${tag}`"
        type="danger"
        size="small"
        class="ml-1"
        circle
        plain
        @click.stop="(e) => deleteTag(tag, e)"
      >
        <i-mdi:trash-can />
      </ElButton>
    </div>
  </div>
</template>
